/**
 * Deck statistics: mana curve, average mana value, colour distribution and card
 * type breakdown.
 *
 * ONE definition of "primary type" and of the bucketing rules, shared by the
 * server's `GET /collections/:id/deck-insights` (which serves the mobile app)
 * and, for the classifier, by the web app's `deckTypeUtils.js`. The rules mirror
 * the web's `DeckInsights.jsx` so both clients show the same numbers for the
 * same deck.
 *
 * CommonJS so both the backend (`require`) and the CRA web build (`import`,
 * via webpack interop) can consume it -- the same arrangement `deckRules.js`
 * uses.
 *
 * @module deckInsights
 */

/**
 * Primary-type precedence. A card is counted under the FIRST of these its type
 * line contains, so an artifact creature is a Creature; `Other` is the fallback.
 */
const TYPE_ORDER = [
  'Creature',
  'Planeswalker',
  'Instant',
  'Sorcery',
  'Enchantment',
  'Artifact',
  'Land',
  'Other',
];

/** Mana curve buckets: 0..6, then everything from 7 up in the last one. */
const CMC_BUCKET_COUNT = 8;
const CMC_MAX_INDEX = 7;

/** Colour groups in display order. `M` multicolour, `C` colourless. */
const COLOR_DISPLAY_ORDER = ['W', 'U', 'B', 'R', 'G', 'M', 'C', 'Land'];

/**
 * @param {string|null|undefined} typeLine
 * @returns {string} one of TYPE_ORDER
 */
function getCardPrimaryType(typeLine) {
  if (!typeLine) return 'Other';
  for (const type of TYPE_ORDER) {
    if (type === 'Other') continue;
    if (typeLine.includes(type)) return type;
  }
  return 'Other';
}

/**
 * @typedef {object} InsightRow
 * @property {number|null} [quantity]   copies; falsy counts as 1, as on web
 * @property {number|null} [cmc]        mana value; null/absent counts as 0
 * @property {string[]|null} [colors]   card colours; empty/absent is colourless
 * @property {string|null} [type_line]
 * @property {boolean} [is_sideboard]
 */

/**
 * Computes the statistics over EVERY row it is given -- sideboard and commander
 * included -- because that is what the web's `DeckInsights` does. A row with no
 * card attributes (a cache miss) is counted as a colourless, mana value 0,
 * `Other` card, again as on web.
 *
 * @param {InsightRow[]} rows
 * @returns {{
 *   mana_curve: number[],
 *   average_mana_value: number|null,
 *   color_distribution: Array<{color: string, count: number}>,
 *   type_breakdown: Array<{type: string, count: number}>,
 *   main_card_count: number,
 *   sideboard_card_count: number,
 * }}
 */
function computeDeckInsights(rows) {
  const manaCurve = new Array(CMC_BUCKET_COUNT).fill(0);
  const colorCounts = {};
  const typeCounts = {};
  let totalCmc = 0;
  let totalNonLand = 0;
  let mainCardCount = 0;
  let sideboardCardCount = 0;

  (rows || []).forEach((row) => {
    const quantity = row.quantity || 1;
    const type = getCardPrimaryType(row.type_line);
    const isLand = type === 'Land';

    if (row.is_sideboard) sideboardCardCount += quantity;
    else mainCardCount += quantity;

    typeCounts[type] = (typeCounts[type] || 0) + quantity;

    if (isLand) {
      colorCounts.Land = (colorCounts.Land || 0) + quantity;
      return;
    }

    const cmc = row.cmc || 0;
    manaCurve[Math.min(Math.floor(cmc), CMC_MAX_INDEX)] += quantity;
    totalCmc += cmc * quantity;
    totalNonLand += quantity;

    const colors = row.colors || [];
    let bucket;
    if (colors.length === 0) bucket = 'C';
    else if (colors.length === 1) bucket = colors[0];
    else bucket = 'M';
    colorCounts[bucket] = (colorCounts[bucket] || 0) + quantity;
  });

  return {
    mana_curve: manaCurve,
    // Rounded the way web displays it (`toFixed(1)`), so both clients agree.
    average_mana_value: totalNonLand > 0 ? Number((totalCmc / totalNonLand).toFixed(1)) : null,
    color_distribution: COLOR_DISPLAY_ORDER
      .filter((color) => (colorCounts[color] || 0) > 0)
      .map((color) => ({ color, count: colorCounts[color] })),
    type_breakdown: TYPE_ORDER
      .filter((type) => (typeCounts[type] || 0) > 0)
      .map((type) => ({ type, count: typeCounts[type] })),
    main_card_count: mainCardCount,
    sideboard_card_count: sideboardCardCount,
  };
}

module.exports = {
  TYPE_ORDER,
  getCardPrimaryType,
  computeDeckInsights,
};
